import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { api, getBackendUrl } from '../../../lib/api';
import { toast } from 'sonner';
import {
  FileText, Upload, Download, Trash2, Loader2, Paperclip, ShieldCheck, Lock,
  CreditCard, Award, Mail, FileCheck2, FileIcon
} from 'lucide-react';
import { formatDate } from '../../../lib/utils';

const DOC_TYPES = [
  { value: 'identity_card', label: "Carte d'identité", icon: CreditCard },
  { value: 'passport', label: 'Passeport', icon: CreditCard },
  { value: 'certificate', label: 'Certificat / Diplôme', icon: Award },
  { value: 'recommendation_letter', label: 'Lettre de recommandation', icon: Mail },
  { value: 'contract', label: 'Contrat signé', icon: FileCheck2 },
  { value: 'other', label: 'Autre', icon: FileIcon },
];

const humanSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const CandidateDocumentsTab = ({ candidateId, candidate, canModify }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('certificate');
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/candidates/${candidateId}/documents`);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (candidateId) load();
  }, [candidateId]);

  const handleUpload = async () => {
    if (!file || !docType) {
      toast.error('Veuillez choisir un type et un fichier.');
      return;
    }
    const fd = new FormData();
    fd.append('document_type', docType);
    fd.append('file', file);
    if (description) fd.append('description', description);
    setUploading(true);
    try {
      const res = await fetch(`${getBackendUrl()}/api/admin/candidates/${candidateId}/documents`, {
        method: 'POST',
        body: fd,
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`,
        },
      });
      if (res.ok) {
        toast.success('Document ajouté');
        setFile(null);
        setDescription('');
        setDocType('certificate');
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Échec de l'upload");
      }
    } catch (e) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/admin/candidates/${candidateId}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token') || ''}` },
      });
      if (!res.ok) {
        toast.error('Téléchargement impossible');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur de téléchargement');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.filename} » ? Cette action est définitive.`)) return;
    const res = await api.delete(`/admin/candidates/${candidateId}/documents/${doc.id}`);
    if (res.ok) {
      toast.success('Document supprimé');
      load();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Suppression impossible');
    }
  };

  const rgpd = candidate?.rgpd_consent;

  return (
    <div className="space-y-4 md:space-y-6" data-testid="candidate-documents-tab">
      {/* RGPD audit trail summary */}
      {rgpd && (
        <Card className="p-4 md:p-5 border-tp-border bg-green-50/40" data-testid="rgpd-audit-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-tp-text mb-1">Consentement RGPD — audit trail</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs mt-2">
                <div className="rounded border border-green-200 bg-white p-2">
                  <p className="text-tp-text-light">Date d'acceptation</p>
                  <p className="font-semibold text-tp-text">{formatDate(rgpd.accepted_at)}</p>
                </div>
                <div className="rounded border border-green-200 bg-white p-2">
                  <p className="text-tp-text-light">Identité acceptée</p>
                  <p className="font-semibold text-tp-text truncate">{rgpd.full_name_at_acceptance}</p>
                  <p className="text-[10px] text-tp-text-light truncate">{rgpd.email_at_acceptance}</p>
                </div>
                <div className="rounded border border-green-200 bg-white p-2">
                  <p className="text-tp-text-light">IP / Navigateur</p>
                  <p className="font-mono text-[11px] text-tp-text">{rgpd.ip_address || '—'}</p>
                  <p className="text-[10px] text-tp-text-light truncate" title={rgpd.user_agent}>{(rgpd.user_agent || '').slice(0, 40) || '—'}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {rgpd.accepted_data_accuracy && <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Exactitude des données</span>}
                {rgpd.accepted_data_storage && <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Stockage confidentiel</span>}
                {rgpd.accepted_data_sharing && <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Partage avec recruteurs</span>}
                {rgpd.consent_text_hash && <span className="text-[11px] bg-white border border-green-200 text-green-700 px-2 py-0.5 rounded font-mono">Charte {rgpd.consent_text_hash}</span>}
              </div>
            </div>
          </div>
        </Card>
      )}
      {!rgpd && (
        <Card className="p-4 border-tp-border bg-orange-50/60">
          <p className="text-xs text-orange-800 flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Aucun consentement RGPD enregistré pour ce candidat (candidat créé avant la mise en place).
          </p>
        </Card>
      )}

      {/* Upload panel */}
      {canModify && (
        <Card className="p-4 md:p-5 border-tp-border" data-testid="upload-panel">
          <div className="flex items-center gap-2 mb-3">
            <Upload className="h-4 w-4 text-tp-primary" />
            <h3 className="text-sm font-semibold text-tp-text">Ajouter un document</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="upload-doc-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`upload-doc-type-${t.value}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={uploading}
              data-testid="upload-doc-file"
            />
          </div>
          <Textarea
            placeholder="Description (optionnel)"
            rows={2}
            className="mt-3"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
            data-testid="upload-doc-description"
          />
          <div className="flex justify-end mt-3">
            <Button
              size="sm"
              onClick={handleUpload}
              disabled={uploading || !file}
              className="bg-tp-accent hover:bg-tp-accent-light text-white"
              data-testid="upload-doc-submit"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              Ajouter
            </Button>
          </div>
          <p className="text-[11px] text-tp-text-light mt-2">
            PDF, JPG, PNG, DOC, DOCX — max 25 Mo. Les documents sont stockés de manière sécurisée.
          </p>
        </Card>
      )}
      {!canModify && (
        <Card className="p-3 bg-orange-50/60 border-orange-200 flex items-center gap-2 text-xs text-orange-700">
          <Lock className="h-4 w-4" />
          Vous n'êtes pas l'admin assigné à ce candidat — consultation uniquement.
        </Card>
      )}

      {/* Documents list */}
      <Card className="border-tp-border">
        <div className="p-4 md:p-5 border-b border-tp-border flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-tp-primary" />
          <h3 className="text-sm font-semibold text-tp-text">Documents ({documents.length})</h3>
        </div>
        <div className="p-4 md:p-5">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-tp-accent" /></div>
          ) : documents.length === 0 ? (
            <p className="text-sm text-tp-text-light italic text-center py-6" data-testid="documents-empty">
              Aucun document n'a encore été ajouté pour ce candidat.
            </p>
          ) : (
            <ul className="divide-y divide-tp-border" data-testid="documents-list">
              {documents.map((doc) => {
                const typeInfo = DOC_TYPES.find((t) => t.value === doc.document_type) || DOC_TYPES[5];
                const Icon = typeInfo.icon;
                return (
                  <li key={doc.id} className="py-3 flex items-start gap-3" data-testid={`doc-row-${doc.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-tp-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-tp-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-tp-text truncate">{doc.filename}</span>
                        <span className="text-[10px] bg-tp-bg px-1.5 py-0.5 rounded text-tp-text-light">{typeInfo.label}</span>
                      </div>
                      <p className="text-[11px] text-tp-text-light mt-0.5">
                        {humanSize(doc.size_bytes)} · ajouté le {formatDate(doc.uploaded_at)}
                        {doc.uploaded_by ? ` par ${doc.uploaded_by}` : ''}
                      </p>
                      {doc.description && <p className="text-xs text-tp-text mt-1">{doc.description}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`doc-download-${doc.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      {canModify && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(doc)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`doc-delete-${doc.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CandidateDocumentsTab;
